import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { getDashboard } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ListTodo, 
  Clock, 
  CheckCircle2, 
  AlertTriangle,
  MessageSquare,
  ArrowRight,
  Loader2,
  TrendingUp,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';

const priorityColors = {
  'Urgente': 'bg-red-100 text-red-700 border-red-200',
  'Alta': 'bg-orange-100 text-orange-700 border-orange-200',
  'Media-Alta': 'bg-amber-100 text-amber-700 border-amber-200',
  'Media': 'bg-blue-100 text-blue-700 border-blue-200',
  'Media-Baja': 'bg-slate-100 text-slate-700 border-slate-200',
  'Baja': 'bg-stone-100 text-stone-600 border-stone-200',
};

const statusColors = {
  'Pendiente': 'bg-amber-100 text-amber-700',
  'En Proceso': 'bg-blue-100 text-blue-700',
  'Completada': 'bg-emerald-100 text-emerald-700',
  'Bloqueada': 'bg-red-100 text-red-700',
  'Cancelada': 'bg-slate-100 text-slate-500',
};

const DashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await getDashboard();
      setData(response.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Error al cargar el dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-10" data-testid="dashboard-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-semibold text-slate-900 font-['Manrope'] tracking-tight">
            Panel de Control
          </h1>
          <p className="text-slate-500 mt-1">Resumen de actividad de UGOLF ARAVELL</p>
        </div>

        {/* Stats Grid - Bento Style */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-8">
          {/* Total Active Tasks */}
          <Card className="col-span-6 md:col-span-3 bg-white border border-stone-200 shadow-sm" data-testid="stat-active-tasks">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Tareas Activas</p>
                  <p className="text-4xl font-bold text-slate-900 mt-2">{data?.total_active_tasks || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Activity className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total Tasks */}
          <Card className="col-span-6 md:col-span-3 bg-white border border-stone-200 shadow-sm" data-testid="stat-total-tasks">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Total Tareas</p>
                  <p className="text-4xl font-bold text-slate-900 mt-2">{data?.total_tasks || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <ListTodo className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Completed */}
          <Card className="col-span-6 md:col-span-3 bg-white border border-stone-200 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Completadas</p>
                  <p className="text-4xl font-bold text-emerald-600 mt-2">{data?.tasks_by_status?.['Completada'] || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pending Queries */}
          <Card className="col-span-6 md:col-span-3 bg-white border border-stone-200 shadow-sm" data-testid="stat-pending-queries">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Consultas Pendientes</p>
                  <p className="text-4xl font-bold text-amber-600 mt-2">{data?.query_stats?.pending || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Urgent Tasks */}
          <Card className="lg:col-span-8 bg-white border border-stone-200 shadow-sm" data-testid="urgent-tasks-card">
            <CardHeader className="border-b border-stone-100 pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Tareas Prioritarias
                </CardTitle>
                <Link to="/tasks?priority=Alta,Urgente">
                  <Button variant="ghost" size="sm" className="text-slate-600 hover:text-slate-900">
                    Ver todas
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {data?.urgent_tasks?.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
                  <p>No hay tareas urgentes pendientes</p>
                </div>
              ) : (
                <div className="divide-y divide-stone-100">
                  {data?.urgent_tasks?.slice(0, 5).map((task) => (
                    <Link 
                      key={task.id} 
                      to={`/tasks/${task.id}`}
                      className="flex items-center justify-between p-4 hover:bg-stone-50 transition-colors group"
                      data-testid={`urgent-task-${task.sequential_number}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono text-slate-400">{task.sequential_number}</span>
                          <Badge className={`${priorityColors[task.priority]} text-[10px] font-bold uppercase`}>
                            {task.priority}
                          </Badge>
                        </div>
                        <p className="font-medium text-slate-900 truncate group-hover:text-emerald-700 transition-colors">
                          {task.title}
                        </p>
                        <p className="text-sm text-slate-500 truncate">{task.functional_area}</p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-slate-600 transition-colors" />
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Priority Distribution */}
          <Card className="lg:col-span-4 bg-white border border-stone-200 shadow-sm">
            <CardHeader className="border-b border-stone-100 pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                <TrendingUp className="w-5 h-5 text-slate-600" />
                Por Prioridad
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-3">
                {Object.entries(data?.tasks_by_priority || {}).map(([priority, count]) => (
                  <div key={priority} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${priorityColors[priority]?.split(' ')[0]}`} />
                      <span className="text-sm text-slate-600">{priority}</span>
                    </div>
                    <span className="font-semibold text-slate-900">{count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Status Distribution */}
          <Card className="lg:col-span-6 bg-white border border-stone-200 shadow-sm">
            <CardHeader className="border-b border-stone-100 pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                <Clock className="w-5 h-5 text-slate-600" />
                Por Estado
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(data?.tasks_by_status || {}).map(([status, count]) => (
                  <div 
                    key={status} 
                    className={`p-3 rounded-lg ${statusColors[status]} flex items-center justify-between`}
                  >
                    <span className="text-sm font-medium">{status}</span>
                    <span className="font-bold">{count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Query Stats */}
          <Card className="lg:col-span-6 bg-white border border-stone-200 shadow-sm">
            <CardHeader className="border-b border-stone-100 pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                  <MessageSquare className="w-5 h-5 text-slate-600" />
                  Consultas
                </CardTitle>
                <Link to="/queries">
                  <Button variant="ghost" size="sm" className="text-slate-600 hover:text-slate-900">
                    Ver todas
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-amber-50 rounded-lg">
                  <p className="text-2xl font-bold text-amber-600">{data?.query_stats?.pending || 0}</p>
                  <p className="text-xs text-amber-700 font-medium">Pendientes</p>
                </div>
                <div className="text-center p-3 bg-emerald-50 rounded-lg">
                  <p className="text-2xl font-bold text-emerald-600">{data?.query_stats?.converted || 0}</p>
                  <p className="text-xs text-emerald-700 font-medium">Convertidas</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-600">{data?.query_stats?.rejected || 0}</p>
                  <p className="text-xs text-slate-500 font-medium">Rechazadas</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default DashboardPage;
